// src/beta/components/ScoreBars.tsx
import type { ScoreParts } from '@/beta/types/showcase';

interface ScoreBarsProps {
  parts: ScoreParts | undefined;
}

const ROW_ORDER: { key: keyof ScoreParts; label: string }[] = [
  { key: 'identity', label: 'Identity' },
  { key: 'provenance', label: 'Provenance' },
  { key: 'documents', label: 'Documents' },
  { key: 'matching', label: 'Matching' },
  { key: 'photos', label: 'Photos' },
  { key: 'multi_source', label: 'Multi-source' },
  { key: 'anchor', label: 'Anchor' },
];

const clamp01 = (n: number) => Math.max(0, Math.min(1, n));

function fillColor(score: number): string {
  if (score >= 0.66) return 'var(--vm-red)';
  if (score >= 0.33) return 'var(--vm-copper-rule)';
  return 'var(--vm-stone)';
}

/** Visualization of `verification.parts`. One row per part — label,
 *  bar with proportional red/copper/stone fill, percentage. Renders
 *  nothing when parts is undefined (verification stub case). */
export function ScoreBars({ parts }: ScoreBarsProps) {
  if (!parts) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--vm-s-2)' }}>
      {ROW_ORDER.map(({ key, label }) => {
        const raw = parts[key] ?? 0;
        const score = clamp01(raw);
        const pct = Math.round(score * 100);
        return (
          <div
            key={key}
            data-testid="score-row"
            style={{
              display: 'grid',
              gridTemplateColumns: '110px 1fr 48px',
              alignItems: 'center',
              gap: 'var(--vm-s-2)',
            }}
          >
            <span
              style={{
                fontFamily: 'var(--vm-font-heading)',
                fontSize: 10,
                letterSpacing: '0.3em',
                textTransform: 'uppercase',
                color: 'var(--vm-stone-light)',
              }}
            >
              {label}
            </span>
            <div
              style={{
                height: 6,
                background: 'var(--vm-charcoal)',
                position: 'relative',
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${pct}%`,
                  background: fillColor(score),
                  transition: 'width 350ms ease',
                }}
              />
            </div>
            <span
              style={{
                fontFamily: 'var(--vm-font-body)',
                fontSize: 12,
                color: 'var(--vm-warm-white)',
                textAlign: 'right',
              }}
            >
              {pct}%
            </span>
          </div>
        );
      })}
    </div>
  );
}
